<template>
  <bubble-menu
    v-if="editor"
    :editor="editor"
    :should-show="shouldShow"
    :options="{ onShow, onHide }"
  >
    <div
      class="menububble"
      :class="{
        '-is-open': state.inputFieldOpen
      }"
    >
      <form
        v-if="state.inputFieldOpen"
        class="menububble__form"
        @submit.prevent="setAndConfirm(state.linkUrl)"
      >
        <input-text
          ref="linkInput"
          v-model:value="state.linkUrl"
          class="menububble__input"
          placeholder="https://"
          @keydown.esc="hideLinkMenu()"
          @enter="setAndConfirm(state.linkUrl)"
        >
          <template #buttons>
            <button
              type="button"
              class="button-icon input__icon"
              @click="setAndConfirm(state.linkUrl)"
            >
              <lucide-icon name="check" />
            </button>
          </template>
        </input-text>
      </form>

      <template v-else>
        <div class="menububble__button">
          <a
            :href="state.linkUrl"
            target="_blank"
            noreferrer
            class="menububble__link"
          >
            <lucide-icon name="link" class="text-12 mr-2" />
            <span>{{ state.linkUrl }}</span>
          </a>
          <button
            type="button"
            class="button-icon pr-1"
            :title="$t('actions.editLink')"
            @click="showLinkMenu()"
          >
            <lucide-icon name="pencil" />
          </button>
          <button
            type="button"
            class="button-icon pl-1"
            :title="$t('actions.deleteLink')"
            @click="unsetLink()"
          >
            <lucide-icon name="trash-2" />
          </button>
        </div>
      </template>
    </div>
  </bubble-menu>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick } from "vue";
import { isMarkActive, getMarkAttributes } from "@tiptap/vue-3";
import { BubbleMenu } from "@tiptap/vue-3/menus";
import { EditorView } from "prosemirror-view";
import { EditorState } from "prosemirror-state";
import { Editor } from "@tiptap/core";

import InputText from "@core/input/InputText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

const props = defineProps<{
  editor?: Editor;
}>();

const linkInput = ref<InstanceType<typeof InputText>>();

const state = reactive({
  linkUrl: undefined as string | undefined,
  inputFieldOpen: false
});

// const currentTippyInstance = ref<TippyInstance>();
const restarting = ref(false);

function getCurrentLink() {
  if (!props.editor) return "";
  return props.editor.getAttributes("link")?.href || "";
}

function onShow() {
  if (getCurrentLink()) state.linkUrl = getCurrentLink();
  if (state.inputFieldOpen) {
    focusLinkInput();
  }
}

function onHide() {
  if (restarting.value == false) {
    state.inputFieldOpen = false;
  }
}

function focusLinkInput() {
  if (linkInput.value) {
    linkInput.value.focus();
    linkInput.value.select();
  }
}

// function onBeforeUpdate(instance: TippyInstance) {
//   currentTippyInstance.value = instance;
//   // HACK: fix positioning.
//   // The link in the popup is not updated quick enough
//   // to be considered in sizing.
//   // Therefore, we set it manually before the menu is shown.
//   const popperLink: HTMLAnchorElement | null = instance.popper.querySelector(
//     ".menububble__link > span"
//   );
//   if (popperLink) {
//     popperLink.innerText = state.linkUrl || "";
//   }

//   restarting.value = false;
// }

function shouldShow(props: {
  editor: Editor;
  view: EditorView;
  state: EditorState;
  oldState?: EditorState | undefined;
  from: number;
  to: number;
}) {
  const isLink = isMarkActive(props.state, "link");

  if (isLink) {
    // Update the internal url var as soon as we're on a valid link.
    const url = getMarkAttributes(props.state, "link")?.href || "";
    if (url != getCurrentLink()) {
      state.inputFieldOpen = false;
      state.linkUrl = url;
    }
  }

  return isLink;
}

function showLinkMenu(href?: string) {
  state.linkUrl = href || getCurrentLink();

  state.inputFieldOpen = true;
  reopen();
}

function hideLinkMenu() {
  state.inputFieldOpen = false;
  reopen();
}

function unsetLink() {
  if (!props.editor) return;
  props.editor.chain().extendMarkRange("link").unsetLink().focus().run();
}

function setLink(url: string | undefined, focus = true) {
  if (!props.editor) return;
  if (url) {
    const chain = props.editor
      .chain()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank" });

    if (focus) {
      chain.focus().run();
    } else {
      chain.run();
    }
  } else {
    unsetLink();
  }
  state.inputFieldOpen = false;
}

function setAndConfirm(url: string | undefined) {
  setLink(url);
  hideLinkMenu();
}

function reopen() {
  nextTick(() => {
    focusLinkInput();
  });
}

defineExpose({
  showLinkMenu,
  hideLinkMenu,
  setLink,
  unsetLink
});
</script>

<style lang="scss">
.menububble {
  position: relative;
  min-width: 12rem;
  // position: absolute;
  // transform: translateX(-50%);
  bottom: 0;
  --bg-color: var(--color-background-medium);
  filter: drop-shadow(0 2px 2px var(--color-shadow-2));

  &.-is-open {
    --bg-color: var(--color-action);
  }

  &__form {
    display: flex;
    position: relative;
  }

  &__input {
    min-width: 12rem;
    font-size: 12px;
  }

  &__button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) min-content min-content;
    white-space: nowrap;
    position: relative;
    background-color: var(--bg-color);
    border-radius: 5px;
    border: none;
    padding-left: 0.75em;
    font-size: 1rem;
    gap: 0.25em;
    align-items: center;
  }
  &__link {
    color: var(--color-text);
    font-weight: 500;
    margin-top: 1px;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    &:hover {
      color: var(--color-action);
      text-decoration: underline;
      text-underline-offset: 2px;
      text-decoration-color: var(--color-action-light);
    }
  }
}
</style>
